import { isSmallOlympicVariant } from './teacher-registration-categories';

/** Oczekiwana nazwa okręgu opiekuna (guardian) na podstawie miasta / ZIP / wariantu. */

const normalize = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

/** Kody pocztowe przypisane do woj. mazowieckiego (prefiks 2 cyfry). */
const MAZOWIECKIE_ZIP_PREFIXES = new Set(['00', '01', '02', '03', '04', '05', '06', '07', '08', '09']);
/** SP: lewobrzeżna Warszawa → mazowieckie I. */
const SMALL_I_ZIP_PREFIXES = new Set(['00', '01', '02']);
/** Duża: cała Warszawa (w tym prawobrzeżna 03/04) → Warszawa. */
const BIG_WARSZAWA_ZIP_PREFIXES = new Set(['00', '01', '02', '03', '04']);

export function zipPrefix(zipCode: string): string {
	return zipCode.replace(/\s+/g, '').slice(0, 2);
}

export function isWarsawCity(city: string): boolean {
	return normalize(city) === 'warszawa';
}

/** Para okręgów mazowieckich bieżącego wariantu (nie mylić nazw duża vs SP). */
function mazowieckieSplitNames(
	districtNames: string[],
	small: boolean,
): { warsawSide: string; restSide: string } | null {
	const warsawSide = small ? 'mazowieckie I' : 'Warszawa';
	const restSide = small ? 'mazowieckie II' : 'Mazowsze';
	if (districtNames.includes(warsawSide) && districtNames.includes(restSide)) {
		return { warsawSide, restSide };
	}
	return null;
}

/**
 * Oczekiwany okręg opiekuna — model per wariant:
 * SP: I = 00–02, II = reszta Mazowsza; duża: Warszawa = 00–04, Mazowsze = reszta.
 */
export function expectedGuardianDistrictName(
	city: string,
	zipCode: string,
	districtNames: string[],
	small = isSmallOlympicVariant(),
): string | null {
	const names = districtNames.filter(Boolean);
	if (names.length === 0) {
		return null;
	}

	const cityNorm = normalize(city);
	if (!small) {
		if (cityNorm === 'krakow' && names.includes('Kraków')) {
			return 'Kraków';
		}
	}

	const mazSplit = mazowieckieSplitNames(names, small);
	const prefix = zipPrefix(zipCode);
	if (mazSplit && /^\d{2}$/.test(prefix) && MAZOWIECKIE_ZIP_PREFIXES.has(prefix)) {
		if (small) {
			const warsaw = isWarsawCity(city);
			if (SMALL_I_ZIP_PREFIXES.has(prefix)) {
				return warsaw ? mazSplit.warsawSide : null;
			}
			return mazSplit.restSide;
		}
		// Duża: ZIP 00–04 → Warszawa; reszta Mazowsza → Mazowsze
		if (BIG_WARSZAWA_ZIP_PREFIXES.has(prefix)) {
			return mazSplit.warsawSide;
		}
		return mazSplit.restSide;
	}

	for (const districtName of names) {
		if (normalize(districtName) === cityNorm) {
			return districtName;
		}
	}

	return null;
}

export function guardianDistrictMismatchMessage(selectedDistrict: string, expected: string | null): string | null {
	if (!expected || selectedDistrict === expected) {
		return null;
	}
	return `Dla podanego kodu pocztowego i miasta wybierz okręg „${expected}”.`;
}

export function guardianDistrictValidation(
	selectedDistrict: string,
	city: string,
	zipCode: string,
	districtNames: string[],
	small = isSmallOlympicVariant(),
): string | null {
	const names = districtNames.filter(Boolean);
	const mazSplit = mazowieckieSplitNames(names, small);
	const prefix = zipPrefix(zipCode);

	if (
		mazSplit &&
		(selectedDistrict === mazSplit.warsawSide || selectedDistrict === mazSplit.restSide) &&
		!/^\d{2}$/.test(prefix)
	) {
		return 'Podaj pełny kod pocztowy (5 cyfr), aby dopasować okręg opiekuna.';
	}

	if (
		mazSplit &&
		(selectedDistrict === mazSplit.warsawSide || selectedDistrict === mazSplit.restSide) &&
		!MAZOWIECKIE_ZIP_PREFIXES.has(prefix)
	) {
		return small
			? 'Okręgi mazowieckie I i II dotyczą kodów pocztowych 00–09. Dla podanego kodu i miasta wybierz właściwy okręg.'
			: 'Okręgi Warszawa i Mazowsze dotyczą kodów pocztowych 00–09. Dla podanego kodu i miasta wybierz właściwy okręg.';
	}

	const expected = expectedGuardianDistrictName(city, zipCode, districtNames, small);
	const mismatch = guardianDistrictMismatchMessage(selectedDistrict, expected);
	if (mismatch) {
		return mismatch;
	}

	if (!mazSplit || !small) {
		return null;
	}

	// SP: 00–02 wymagają miasta Warszawa
	if (SMALL_I_ZIP_PREFIXES.has(prefix)) {
		if (!isWarsawCity(city)) {
			return 'Dla kodów pocztowych 00-, 01- i 02- wpisz miasto „Warszawa”.';
		}
		if (selectedDistrict !== mazSplit.warsawSide) {
			return `Dla podanego kodu pocztowego i miasta wybierz okręg „${mazSplit.warsawSide}”.`;
		}
	}

	return null;
}
